import React, { FC, useState } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import { Tag } from '../hooks/useChat'

type Props = {
    show: boolean
    onHide: () => void
    contactId: number
    currentTags: Tag[]
    availableTags: Tag[]
    onCreateTag: (name: string, color: string) => Promise<any>
    onManageTag: (contactId: number, tagId: number, action: 'add' | 'remove') => Promise<any>
}

const COLORS = [
    '#3E97FF', // Blue
    '#F1416C', // Red
    '#50CD89', // Green
    '#FFc700', // Yellow
    '#7239EA', // Purple
    '#009EF7', // Light Blue
    '#181C32', // Dark
]

const TagManager: FC<Props> = ({
    show,
    onHide,
    contactId,
    currentTags,
    availableTags,
    onCreateTag,
    onManageTag
}) => {
    const [view, setView] = useState<'list' | 'create'>('list')
    const [newTagName, setNewTagName] = useState('')
    const [newTagColor, setNewTagColor] = useState(COLORS[0])
    const [submitting, setSubmitting] = useState(false)

    const handleCreate = async () => {
        if (!newTagName) return
        setSubmitting(true)
        try {
            await onCreateTag(newTagName, newTagColor)
            setView('list')
            setNewTagName('')
            setNewTagColor(COLORS[0])
        } catch (e) {
            // Error handled in hook or could set local error
        } finally {
            setSubmitting(false)
        }
    }

    const toggleTag = async (tag: Tag) => {
        const isAssigned = currentTags.some(t => t.id === tag.id)
        const action = isAssigned ? 'remove' : 'add'

        try {
            await onManageTag(contactId, tag.id, action)
        } catch (e) {
            // Error
        }
    }

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {view === 'list' ? 'Manage Tags' : 'New Tag'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {view === 'list' ? (
                    <>
                        <div className='mb-5'>
                            <label className='form-label fw-bold'>Available Tags</label>
                            <div className='d-flex flex-wrap gap-2'>
                                {availableTags.map(tag => {
                                    const isAssigned = currentTags.some(t => t.id === tag.id)
                                    return (
                                        <span
                                            key={tag.id}
                                            className={`badge cursor-pointer px-3 py-2 ${isAssigned ? '' : 'bg-light text-gray-600 border border-gray-300'}`}
                                            style={isAssigned ? { backgroundColor: tag.color, color: 'white' } : {}}
                                            onClick={() => toggleTag(tag)}
                                        >
                                            {tag.name}
                                            {isAssigned && <i className='bi bi-check text-white ms-1'></i>}
                                        </span>
                                    )
                                })}
                                {availableTags.length === 0 && (
                                    <span className='text-muted'>No tags created yet.</span>
                                )}
                            </div>
                        </div>

                        <div className='d-grid'>
                            <button className='btn btn-light-primary btn-sm' onClick={() => setView('create')}>
                                <i className='bi bi-plus-lg'></i> Create New Tag
                            </button>
                        </div>
                    </>
                ) : (
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Ex: VIP Client"
                                value={newTagName}
                                onChange={(e) => setNewTagName(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Color</Form.Label>
                            <div className='d-flex gap-2 flex-wrap'>
                                {COLORS.map(color => (
                                    <div
                                        key={color}
                                        className={`rounded-circle cursor-pointer ${newTagColor === color ? 'border border-3 border-dark' : ''}`}
                                        style={{ width: '30px', height: '30px', backgroundColor: color }}
                                        onClick={() => setNewTagColor(color)}
                                    />
                                ))}
                            </div>
                        </Form.Group>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                {view === 'create' ? (
                    <>
                        <Button variant="light" onClick={() => setView('list')}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleCreate} disabled={!newTagName || submitting}>
                            {submitting ? 'Saving...' : 'Create'}
                        </Button>
                    </>
                ) : (
                    <Button variant="light" onClick={onHide}>
                        Close
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default TagManager
